import { formatCompactOpeningName } from '../games/shared/openingLabel';

/** First full moves shown in opening labels and game summaries. */
export const OPENING_FULL_MOVE_LIMIT = 10;
export const OPENING_SAN_LIMIT = OPENING_FULL_MOVE_LIMIT * 2;

export const ECO_OPENING_LABEL_MAX_LENGTH = 48;

export type EcoOpeningFields = {
  eco?: string;
  opening?: string;
  /** Full variation name from the lichess openings reference table. */
  lichessOpening?: string;
  /** Explorer game rows use `event` for the PGN opening name. */
  event?: string;
};

function truncateLabel(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

/** Format `B20 · Sicilian Defense`. */
export function formatEcoOpeningLabel(fields: EcoOpeningFields): string {
  const opening =
    fields.lichessOpening?.trim() ||
    fields.opening?.trim() ||
    fields.event?.trim();
  const eco = fields.eco?.trim();

  if (eco && opening) {
    return `${eco} · ${opening}`;
  }
  if (opening) {
    return opening;
  }
  if (eco) {
    return eco;
  }
  return '—';
}

/** Format `B20 · Sicilian Defense` (truncated). */
export function formatTruncatedEcoOpening(
  fields: EcoOpeningFields,
  maxLength = ECO_OPENING_LABEL_MAX_LENGTH,
): string {
  const label = formatEcoOpeningLabel(fields);
  if (label === '—') {
    return label;
  }
  return truncateLabel(label, maxLength);
}

function resolveOpeningName(fields: EcoOpeningFields): string | undefined {
  return (
    fields.lichessOpening?.trim() ||
    fields.opening?.trim() ||
    fields.event?.trim() ||
    undefined
  );
}

/** Compact opening label for board headers — no ECO prefix. */
export function formatCompactBoardOpeningLabel(fields: EcoOpeningFields): string {
  const opening = resolveOpeningName(fields);
  if (!opening) {
    return '—';
  }
  return formatCompactOpeningName(opening);
}

/** Truncated compact opening label for inline game rows. */
export function formatTruncatedCompactBoardOpeningLabel(
  fields: EcoOpeningFields,
  maxLength = ECO_OPENING_LABEL_MAX_LENGTH,
): string {
  const label = formatCompactBoardOpeningLabel(fields);
  if (label === '—') {
    return label;
  }
  return truncateLabel(label, maxLength);
}

/** Full opening label for board-header tooltips (includes ECO when available). */
export function formatBoardOpeningTooltip(fields: EcoOpeningFields): string {
  return formatEcoOpeningLabel(fields);
}

/** Format SAN plies as `1.e4 e5 2.Nf3 Nf6`. */
export function formatNumberedOpeningSans(
  openingSans: string[] | undefined,
  limit = OPENING_SAN_LIMIT,
): string {
  const moves = (openingSans ?? []).slice(0, limit);
  if (moves.length === 0) {
    return '—';
  }

  const parts: string[] = [];
  for (let i = 0; i < moves.length; i += 2) {
    const moveNumber = i / 2 + 1;
    const white = moves[i];
    const black = moves[i + 1];
    parts.push(
      black ? `${moveNumber}.${white} ${black}` : `${moveNumber}.${white}`,
    );
  }

  return parts.join(' ');
}
